import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  Card,
  Collapse,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Select,
  Empty,
} from "antd";
import GeoToberWallSectionBuildUp from "./GeoToberWallSectionBuildUp";
import {
  geoToberWallSpecifications,
  GeoToberWallSpecification,
  geoToberWallCategories,
  geoToberWallFieldToSpecId,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
  BuildUpItem,
} from "../../../mock/geo-tober-wall-build-up";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { useTender } from "../../../contexts/TenderContext";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import {
  useGetGeoToberWallBuildUp,
  GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS,
} from "../../../hooks/useWallSectionBuildUp";
import { saveGeoToberWallBuildUp } from "../../../utils/wallsBuildUpSave";
import { useQueryClient } from "@tanstack/react-query";
import { normaliseDecimalMmInLabel } from "../../../utils/dimensionLabel";
import { getNumericBoqVersion } from "../../../utils/boqVersionPrefix";

const { Title, Text } = Typography;
const { Panel } = Collapse;


const GeoToberWallSpecificationContainer: React.FC = () => {
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [selectedWallType, setSelectedWallType] = useState<string>("all");
  const { settings } = useGlobalSettings();
  const { state: tender, updateField, saveTender } = useTender();
  const queryClient = useQueryClient();
  const [boqVersionNumber, setBoqVersionNumber] = useState<number>(() => {
    const raw = localStorage.getItem("currentBoqVersion");
    const v = raw ? getNumericBoqVersion(JSON.parse(raw)?.version ?? 1) : 1; return v || 1;
  });

  // Re-read boqVersionNumber when localStorage changes (e.g. after save from another component)
  useEffect(() => {
    const handleStorage = () => {
      const raw = localStorage.getItem("currentBoqVersion");
      const v = raw ? getNumericBoqVersion(JSON.parse(raw)?.version ?? 1) : 1;
      setBoqVersionNumber(v || 1);
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("versionDataReloaded", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("versionDataReloaded", handleStorage);
    };
  }, []);

  const { data: persistedBuildUp } = useGetGeoToberWallBuildUp(
    tender.projectName,
    boqVersionNumber,
    { enabled: !!tender.projectName, staleTime: 0 }
  );

  // Hydrate tender context once on mount from persisted localStorage data
  const hasHydrated = useRef(false);

  // Reset hydration flag when version changes so fresh data from a new version is applied
  useEffect(() => {
    hasHydrated.current = false;
  }, [boqVersionNumber, tender.projectName]);

  useEffect(() => {
    // `persistedBuildUp` can resolve to `{}` (no `specifications`) rather than
    // `undefined` when the API has nothing saved yet for this tender/version —
    // still truthy, so the guard above alone doesn't catch it; `?.forEach`
    // below no-ops instead of throwing on that empty-object case (same fix as
    // WallsSpecificationContainer.tsx's identical hydration effect).
    if (!persistedBuildUp || hasHydrated.current) return;
    hasHydrated.current = true;

    const buildUpData: Record<string, Array<{ id: string; rate: number; labour: number; total: number }>> = {};
    const dimensionData: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> = {};

    persistedBuildUp.specifications?.forEach((spec) => {
      buildUpData[spec.id] = spec.items.map((item) => ({
        id: item.id,
        rate: item.rate,
        labour: item.labour,
        total: item.total,
      }));
      dimensionData[spec.id] = spec.items.map((item) => ({
        description: item.description,
        length: item.length ?? 0,
        width: item.width ?? 0,
        height: item.height ?? 0,
        net: item.net,
        unit: item.unit,
      }));
    });

    updateField("groundworks", {
      ...groundworksRef.current,
      geoToberWallBuildUpData: buildUpData,
      geoToberWallBuildUpDimensions: dimensionData,
      geoToberWallColumnHeaders: persistedBuildUp.tobermoreColumnHeaders,
      geoToberWallDimensionValues: persistedBuildUp.tobermoreColumnDimensions,
    });
  }, [persistedBuildUp, updateField]);

  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const getRateFromLibrary = useCallback(
    (evaluateCode: string): number => {
      const items = itemLibraryData as unknown as any[];
      if (!Array.isArray(items) || items.length === 0) return 0;
      const match = items.find(
        (lib) =>
          lib.resourceCode?.trim().toUpperCase() ===
          evaluateCode.trim().toUpperCase(),
      );
      return match?.nettRate ?? 0;
    },
    [itemLibraryData],
  );

  const savedBuildUpData = tender?.groundworks?.geoToberWallBuildUpData || {};

  // Imported net dimensions from "GEOWALL MASTER" / "TOB MASTER" sheets
  // (also populated by the seeding effect and by manual edits in the table).
  const importedBuildUpDimensions: Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>> =
    (tender?.groundworks as any)?.geoToberWallBuildUpDimensions || {};

  // Queue of ids per description, built from one spec's own item list. A
  // single-value map collapses specs that list the same description twice
  // (e.g. Tobermore TPCW's two "Stockpile Onsite" rows) onto one id, so both
  // rows sync together — building this per-spec and consuming ids in item
  // order disambiguates repeated descriptions correctly.
  const buildDescriptionToMockIdQueue = (items: BuildUpItem[]): Map<string, string[]> => {
    const map = new Map<string, string[]>();
    items.forEach((item) => {
      const key = item.description.toLowerCase();
      const queue = map.get(key) ?? [];
      queue.push(item.id);
      map.set(key, queue);
    });
    return map;
  };

  // Refs MUST be updated synchronously during render (not in a useEffect) so
  // that rapid edit handlers read the latest values. The earlier useEffect
  // pattern lagged by one render and caused handleSpecificationChange to
  // spread a stale `groundworksRef.current`, silently reverting other
  // groundworks fields (visible as "data pulls old; refreshes correctly").
  const buildUpDataRef = useRef(savedBuildUpData);
  const groundworksRef = useRef(tender?.groundworks);
  const tenderRef = useRef(tender);
  const itemLibraryRef = useRef(itemLibraryData);
  const ohpRef = useRef(settings.groundworks.overheadAndProfit);
  const prelimsRef = useRef(settings.groundworks.preliminaries);
  const versionRef = useRef(boqVersionNumber);
  buildUpDataRef.current = savedBuildUpData;
  groundworksRef.current = tender?.groundworks;
  tenderRef.current = tender;
  itemLibraryRef.current = itemLibraryData;
  ohpRef.current = settings.groundworks.overheadAndProfit;
  prelimsRef.current = settings.groundworks.preliminaries;
  versionRef.current = boqVersionNumber;

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);


  const handleSpecificationChange = useCallback(
    (specId: string, updatedItems: BuildUpItem[]) => {
      const itemsData = updatedItems.map((item) => ({
        id: item.id,
        rate: item.rate,
        labour: item.labour,
        total: item.total,
      }));

      // Persist length/width/height/net back to dimensions so the BoQ Summary,
      // Excel Export, and Plot Matrix (which all read
      // geoToberWallBuildUpDimensions) see the user's edits.
      const dimensionsData = updatedItems.map((item) => ({
        description: item.description,
        length: item.length ?? 0,
        width: item.width ?? 0,
        height: item.height ?? 0,
        net: item.net,
        unit: item.unit,
      }));

      const updatedBuildUpData = {
        ...buildUpDataRef.current,
        [specId]: itemsData,
      };

      const existingDimensions =
        ((groundworksRef.current as any)?.geoToberWallBuildUpDimensions || {}) as
        Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>>;

      const updatedDimensions = {
        ...existingDimensions,
        [specId]: dimensionsData,
      };

      buildUpDataRef.current = updatedBuildUpData;

      updateField("groundworks", {
        ...groundworksRef.current,
        geoToberWallBuildUpData: updatedBuildUpData,
        geoToberWallBuildUpDimensions: updatedDimensions,
      });

      // Debounced save - save after 1 second of no changes. Also pushes the
      // latest build-up to the dedicated geo-tober walls API so the React
      // Query cache can refetch and any subsequent hydration reads the
      // authoritative server state (previously this only happened on Save &
      // Continue, so mid-session the API would lag the local edits and any
      // re-hydration would clobber them with stale data — "data pulls old;
      // refresh fixes it" was the symptom).
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await saveTender();
          await saveGeoToberWallBuildUp({
            tender: tenderRef.current,
            itemLibraryData: itemLibraryRef.current,
            ohpPercent: ohpRef.current,
            prelimsPercent: prelimsRef.current,
            versionOverride: versionRef.current,
          });
          // Allow the next persistedBuildUp arrival to re-hydrate the context
          // (it would otherwise be blocked by `hasHydrated.current === true`).
          hasHydrated.current = false;
          await queryClient.invalidateQueries({
            queryKey: GEO_TOBER_WALL_BUILD_UP_QUERY_KEYS.detail(
              tenderRef.current.projectName,
              versionRef.current,
            ),
          });
        } catch (err) {
          console.error("Failed to persist geo-tober wall build-up:", err);
        }
      }, 1000);
    },
    [updateField, saveTender, queryClient],
  );

  // Calculate totals for each wall type from the takeoff table
  const wallTypeTotals = useMemo(() => {
    const tableData = tender?.groundworks?.geoToberWallTable || [];
    const totals: Record<string, number> = {};

    Object.keys(geoToberWallFieldToSpecId).forEach((fieldName) => {
      totals[fieldName] = 0;
    });

    tableData.forEach((row: any) => {
      Object.keys(geoToberWallFieldToSpecId).forEach((fieldName) => {
        const value = row[fieldName];
        if (typeof value === "number" && !isNaN(value)) {
          totals[fieldName] += value;
        }
      });
    });

    return totals;
  }, [tender?.groundworks?.geoToberWallTable]);

  // Spec IDs that have quantity > 0
  const activeSpecIds = useMemo(() => {
    return Object.entries(wallTypeTotals)
      .filter(([, total]) => total > 0)
      .map(([fieldName]) => geoToberWallFieldToSpecId[fieldName]);
  }, [wallTypeTotals]);

  const hasAnyData = activeSpecIds.length > 0;

  // Configured TPCW column headers from tender context
  const tpcwColumnHeaders: Record<string, string> =
    (tender?.groundworks as any)?.geoToberWallColumnHeaders || {};
  const tpcwDimensionValues: Record<string, string> =
    (tender?.groundworks as any)?.geoToberWallDimensionValues || {};

  // Seed blank build-up templates for active wall specs that have no imported
  // dimension data yet (manual-entry flow), AND clear stale build-up entries
  // for specs whose take-off quantity has dropped to zero. Takeoff XML import
  // already writes to `geoToberWallBuildUpDimensions` (JobTypeSelection.tsx);
  // this effect mirrors that for manually-added wall types so the BoQ Summary,
  // Excel Export, and Plot Matrix all pick up the template items.
  //
  // Clear-on-zero: when the user removes a wall type from the take-off table
  // (`geoToberWallTable[plot].<field>` total goes to 0), the spec leaves
  // `activeSpecIds` and we drop its build-up entry. If the user later adds
  // quantity back, the seed branch below will recreate the zero template —
  // exactly the contract the user expects (no leftover non-zero L/W/H/Net
  // values carried over from a prior take-off pass).
  useEffect(() => {
    const existing =
      ((groundworksRef.current as any)?.geoToberWallBuildUpDimensions || {}) as
      Record<string, Array<{ description: string; length: number; width: number; height: number; net: number; unit: string }>>;

    const activeSet = new Set(activeSpecIds);
    const seeded = { ...existing };
    let mutated = false;

    // Step 1 — drop build-up entries for specs no longer in activeSpecIds.
    Object.keys(seeded).forEach((specId) => {
      if (!activeSet.has(specId)) {
        delete seeded[specId];
        mutated = true;
      }
    });

    // Step 2 — seed zero templates for active specs that have no entry yet.
    activeSpecIds.forEach((specId) => {
      if (seeded[specId] && seeded[specId].length > 0) return;

      // Dynamic TPCW specs: only seed once a real dimension is configured
      // (otherwise we'd seed against the "TPCW xxxxmm" placeholder)
      const tpcwMatch = specId.match(/^tobermore_(tpcw\d+)$/);
      if (tpcwMatch && !tpcwDimensionValues[tpcwMatch[1]]) return;

      const spec = geoToberWallSpecifications.find((s) => s.id === specId);
      if (!spec) return;

      // Seed the activity list (descriptions + units) from the mock template
      // but zero out every numeric field. The user fills in L / W / H as
      // they take off; Net auto-computes; rate comes from the item library.
      seeded[specId] = spec.items.map((item) => ({
        description: item.description,
        length: 0,
        width: 0,
        height: 0,
        net: 0,
        unit: item.unit,
      }));
      mutated = true;
    });

    if (mutated) {
      updateField("groundworks", {
        ...groundworksRef.current,
        geoToberWallBuildUpDimensions: seeded,
      });
    }
  }, [activeSpecIds, tpcwDimensionValues, updateField]);

  const getTpcwLabel = (fieldKey: string, fallback: string): string => {
    // Return stored column header (from Excel import) exactly as-is.
    if (tpcwColumnHeaders[fieldKey]) return tpcwColumnHeaders[fieldKey];
    // Fallback: build label from dimension value and normalise decimal mm.
    if (tpcwDimensionValues[fieldKey]) return normaliseDecimalMmInLabel(`TPCW ${tpcwDimensionValues[fieldKey]}mm`);
    return fallback;
  };

  // Spec ID → takeoff total (linear metres) for display in panel header
  const specIdToTotal = useMemo(() => {
    const map: Record<string, number> = {};
    Object.entries(geoToberWallFieldToSpecId).forEach(([fieldName, specId]) => {
      map[specId] = wallTypeTotals[fieldName] || 0;
    });
    return map;
  }, [wallTypeTotals]);

  // Filter specifications: only those with quantity > 0, then optionally by wall type
  const filteredSpecifications = useMemo(() => {
    let filtered = geoToberWallSpecifications.filter((spec) => {
      if (!activeSpecIds.includes(spec.id)) return false;
      // For tobermore TPCW specs, only show if a real numeric dimension is configured
      // (excludes "TPCW xxxxmm" placeholder — only shows e.g. "TPCW 900mm")
      const tpcwMatch = spec.id.match(/^tobermore_(tpcw\d+)$/);
      if (tpcwMatch) {
        return !!tpcwDimensionValues[tpcwMatch[1]];
      }
      return true;
    });

    if (selectedWallType !== "all") {
      filtered = filtered.filter((spec) => spec.type === selectedWallType);
    }

    return filtered.map((spec) => {
      // Override name for dynamic TPCW columns using configured headers
      const tpcwMatch = spec.id.match(/^tobermore_(tpcw\d)$/);
      if (tpcwMatch) {
        spec = { ...spec, name: getTpcwLabel(tpcwMatch[1], spec.name) };
      }
      const savedData = savedBuildUpData[spec.id];
      const importedItems = importedBuildUpDimensions[spec.id];
      const codeMap =
        spec.type === "geowall"
          ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP
          : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;

      let mergedItems: BuildUpItem[];

      if (importedItems && importedItems.length > 0) {
        const descriptionToMockIdQueue = buildDescriptionToMockIdQueue(spec.items);
        const descUsedCount = new Map<string, number>();
        // Build items from imported sheet data, filtering blank/numeric-only descriptions
        mergedItems = importedItems.filter((imported) => {
          const desc = String(imported.description ?? "").trim();
          return desc.length > 0 && !/^[0-9.]+$/.test(desc);
        }).map((imported) => {
          const descKey = imported.description.toLowerCase();
          const occurrence = descUsedCount.get(descKey) ?? 0;
          descUsedCount.set(descKey, occurrence + 1);
          const mockId = descriptionToMockIdQueue.get(descKey)?.[occurrence];
          const id = mockId ?? descKey.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
          const evaluateCode = codeMap[id];
          const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode) : 0;
          const savedItem = savedData?.find((s: any) => s.id === id);
          const rate = savedItem?.rate ? (savedItem.rate !== 0 ? savedItem.rate : libraryRate) : libraryRate;
          return {
            id,
            description: imported.description,
            length: imported.length || undefined,
            width:  imported.width  || undefined,
            height: imported.height || undefined,
            net:    imported.net,
            unit:   imported.unit,
            rate,
            labour: savedItem?.labour ?? 0,
            total:  imported.net * rate,
          } as BuildUpItem;
        });
      } else {
        // No imported dimensions yet (manually-added wall type before the
        // seeding effect commits) — fall back to the mock template so the
        // user sees the blank build-up structure immediately.
        mergedItems = spec.items.map((mockItem) => {
          const evaluateCode = codeMap[mockItem.id];
          const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode) : 0;
          const savedItem = savedData?.find((s: any) => s.id === mockItem.id);
          const rate = savedItem?.rate
            ? (savedItem.rate !== 0 ? savedItem.rate : libraryRate)
            : libraryRate;
          return {
            ...mockItem,
            rate,
            labour: savedItem?.labour ?? 0,
            total: mockItem.net * rate,
          } as BuildUpItem;
        });
      }

      const netTotal = mergedItems.reduce((sum, item) => sum + Math.abs(item.total), 0);
      const ohpPercent = settings.groundworks.overheadAndProfit;
      const prelimsPercent = settings.groundworks.preliminaries;
      const markupFactor = (1 + ohpPercent / 100) * (1 + prelimsPercent / 100);
      const ohpAmount = netTotal * (ohpPercent / 100);
      const grandTotal = netTotal * markupFactor;

      return {
        ...spec,
        items: mergedItems,
        netTotal,
        ohpPercent,
        prelimsPercent,
        ohpAmount,
        grandTotal,
      };
    });
  }, [
    selectedWallType,
    activeSpecIds,
    settings.groundworks.overheadAndProfit,
    settings.groundworks.preliminaries,
    savedBuildUpData,
    importedBuildUpDimensions,
    getRateFromLibrary,
    tpcwDimensionValues,
  ]);

  const getWallTypeColor = (specId: string): string => {
    if (specId === "geowall_gpcw600") return "#1890ff";
    if (specId === "geowall_gpcw750") return "#0958d9";
    if (specId === "geowall_rw_900") return "#722ed1";
    if (specId === "geowall_gpcw1050") return "#531dab";
    if (specId.startsWith("tobermore_tpcw")) return "#389e0d";
    return "#666666";
  };

  const wallTypeOptions = useMemo(() => {
    return [
      { value: "all", label: "All Wall Types" },
      { value: "geowall", label: geoToberWallCategories.geowall },
      { value: "tobermore", label: geoToberWallCategories.tobermore },
    ];
  }, []);

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <Title level={4} className="mb-2">
              Geo & Tobermore Wall Section Build Up
            </Title>
            <Text type="secondary" className="block">
              Detailed construction specifications and cost breakdowns for Geo
              Wall and Tobermore Wall types.
            </Text>
          </div>
          <div className="flex items-center space-x-2 mt-4">
            <Text strong>Wall Type:</Text>
            <Select
              value={selectedWallType}
              onChange={(value) => {
                setSelectedWallType(value);
                setActiveKeys([]);
              }}
              style={{ width: 220 }}
              options={wallTypeOptions}
            />
          </div>
        </div>
      </div>

      <Row gutter={[16, 16]} className="mb-6">
        <Col span={6}>
          <Card size="small" className="text-center">
            <Text type="secondary" className="block mb-1">
              Specifications
            </Text>
            <Title level={4} className="mb-0 text-blue-600">
              {filteredSpecifications.length}
            </Title>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" className="text-center">
            <Text type="secondary" className="block mb-1">
              Total Items
            </Text>
            <Title level={4} className="mb-0 text-green-600">
              {filteredSpecifications.reduce(
                (sum, spec) => sum + spec.items.length,
                0,
              )}
            </Title>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" className="text-center">
            <Text type="secondary" className="block mb-1">
              Category
            </Text>
            <Title level={4} className="mb-0 text-purple-600">
              {selectedWallType === "all"
                ? "All Types"
                : geoToberWallCategories[
                    selectedWallType as keyof typeof geoToberWallCategories
                  ]}
            </Title>
          </Card>
        </Col>
      </Row>

      {!hasAnyData && (
        <Empty
          description={
            <span>
              No Geowall or Tobermore data found.
              <br />
              Please add values in the Geowalls or Tobermore Walls tab first.
            </span>
          }
          className="py-12"
        />
      )}

      {filteredSpecifications.length > 0 && (
        <Collapse
          activeKey={activeKeys}
          onChange={(keys) => setActiveKeys(keys as string[])}
          className="geo-tober-specifications-collapse"
          size="large"
        >
          {filteredSpecifications.map((specification: GeoToberWallSpecification) => (
            <Panel
              key={specification.id}
              header={
                <div className="flex justify-between items-center w-full">
                  <Space size="large" align="center">
                    <Tag
                      color={getWallTypeColor(specification.id)}
                      className="font-bold text-sm px-3 py-1"
                      style={{ margin: 0 }}
                    >
                      {specification.name}
                    </Tag>
                    <div className="flex flex-col">
                      <Text strong className="text-base">
                        {specification.category} — {specification.name}
                      </Text>
                      <Text type="secondary" className="text-xs">
                        {specification.items.length} construction items
                      </Text>
                    </div>
                  </Space>
                  <div className="flex items-center gap-6">
                    <div className="flex flex-col items-end">
                      <Text type="secondary" className="text-xs">
                        Takeoff Total
                      </Text>
                      <Text strong className="text-base text-blue-600">
                        {(specIdToTotal[specification.id] || 0).toFixed(3)} m
                      </Text>
                    </div>
                    <div className="flex flex-col items-end">
                      <Text strong className="text-lg text-green-600">
                        £{Math.abs(specification.grandTotal).toFixed(2)}
                      </Text>
                      {((specification.prelimsPercent ?? 0) > 0 || specification.ohpPercent > 0) && (
                        <Text type="secondary" className="text-xs">
                          Inc.{specification.ohpPercent > 0 && ` ${specification.ohpPercent}% OHP`}{specification.ohpPercent > 0 && (specification.prelimsPercent ?? 0) > 0 && ' +'}{(specification.prelimsPercent ?? 0) > 0 && ` ${specification.prelimsPercent}% Prelims`}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              }
              className={`geo-tober-spec-panel spec-${specification.id}`}
              extra={null}
            >
              <GeoToberWallSectionBuildUp
                specification={specification}
                onSpecificationChange={(updatedSpec) =>
                  handleSpecificationChange(
                    specification.id,
                    updatedSpec.items,
                  )
                }
              />
            </Panel>
          ))}
        </Collapse>
      )}
    </div>
  );
};

export default GeoToberWallSpecificationContainer;
